import { getT } from "../../../locales";
import type { EditorElement } from "../../../editor/types";
import type {
  ChoiceAppearance,
  ChoiceAppearanceExtended,
  DragSourceAppearance,
  DropTargetAppearance,
  OrderListAppearance,
  MatchingPointAppearance,
  DropAreaConfig,
  ImageStyle,
  AudioStyle,
  VideoStyle,
  MathTypeStyle,
  LabelPosition,
  // SubmitButtonStyle,
  TextStyle,
  FillInTheBlanksAppearance,
  PaintAreaAppearance,
  PaintSwatchAppearance,
  PushToTalkAppearance,
  TableAppearance,
  // TimerStyle,
} from "../types";

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: "Inter",
  fontSize: 18,
  fontWeight: 400,
  italic: false,
  underline: false,
  color: "#1f2430",
  textAlign: "left",
  lineHeight: 1,
  letterSpacing: 0,
  backgroundColor: "transparent",
  paddingX: 2,
  paddingY: 2,
  borderRadius: 0,
  borderWidth: 0,
  borderColor: "#000000",
  textTransform: "none",
  shadow: "none",
};

export const DEFAULT_CHOICE_APPEARANCE: ChoiceAppearance = {
  fontFamily: "Inter",
  fontSize: 16,
  groupBackground: "transparent",
  groupBorderRadius: 0,
  groupBorderWidth: 0,
  groupBorderColor: "transparent",
  groupPadding: 0,
  optionBackground: "#ffffff",
  optionActiveBackground: "#e1e9ff",
  optionTextColor: "#1f2430",
  optionActiveTextColor: "#1f2430",
  optionBorderWidth: 1,
  optionBorderColor: "#cbd5e1",
  optionActiveBorderColor: "#2563eb",
  optionBorderRadius: 0,
  optionPaddingX: 16,
  optionPaddingY: 12,
  optionSpacing: 10,
  optionAlign: "left",
  indicatorPosition: "leading",
  indicatorColor: "#2563eb",
  indicatorActiveColor: "#1747b5",
};

export const DEFAULT_DRAG_SOURCE_APPEARANCE: DragSourceAppearance = {
  backgroundColor: "#3b82f6",
  textColor: "#ffffff",
  borderColor: "#1d4ed8",
  borderWidth: 2,
  borderRadius: 0,
  paddingX: 16,
  paddingY: 10,
  labelPosition: "center",
  clonable: false, // Varsayılan olarak false, yani öğe sürüklendiğinde orijinal yerinden kalkar
  maxClones: 0, // 0 = sonsuz
  showNumbering: false, // Hedef alanda numaralandırma gösterme
  numberingFontSize: 11,
  numberingTextColor: "#f9fafb",
  numberingBackgroundColor: "rgba(15, 23, 42, 0.9)",
  numberingBorderRadius: 0,
  numberingPosition: "top-left",
  useImage: false,
  imageUrl: "",
  imageAlt: "",
  imageObjectFit: "contain",
  showLabel: true,
};

export const DEFAULT_DROP_TARGET_APPEARANCE: DropTargetAppearance = {
  backgroundColor: "#f8fafc",
  borderColor: "#cbd5e1",
  borderWidth: 2,
  borderStyle: "dashed",
  borderRadius: 0,
  paddingX: 16,
  paddingY: 16,
  labelPosition: "top-center",
  labelTextColor: "#2563eb",
  labelBackground: "rgba(37, 99, 235, 0.12)",
  labelPaddingX: 10,
  labelPaddingY: 6,
  labelBorderRadius: 0,
  labelShadow: "none",
  contentAlignment: "center",
  contentLayout: "vertical",
  itemSpacing: 8,
  useBackgroundImage: false,
  backgroundImageUrl: "",
  backgroundImageFit: "cover",
  backgroundImagePosition: "center center",
  backgroundImageOpacity: 1,
  useGrid: false,
  gridRows: 2,
  gridCols: 3,
  showGridLines: true,
  gridLineColor: "rgba(15,23,42,0.08)",
};

export const DEFAULT_DROP_AREA: DropAreaConfig = {
  enabled: false,
  x: 0,
  y: 0,
  width: 120,
  height: 80,
  showOutline: true,
  outlineColor: "rgba(37, 99, 235, 0.8)",
  outlineWidth: 2,
  highlightEnabled: true,
  highlightColor: "rgba(37, 99, 235, 0.12)",
};

export const DEFAULT_ORDER_LIST_APPEARANCE: OrderListAppearance = {
  backgroundColor: "#f8fafc",
  borderColor: "#cbd5e1",
  borderWidth: 2,
  borderStyle: "dashed",
  borderRadius: 0,
  paddingX: 16,
  paddingY: 16,
  labelPosition: "top-center",
  labelTextColor: "#2563eb",
  labelBackground: "rgba(37, 99, 235, 0.12)",
  labelPaddingX: 10,
  labelPaddingY: 6,
  labelBorderRadius: 0,
  labelShadow: "none",
  contentAlignment: "center",
  contentLayout: "horizontal",
  itemSpacing: 8,
  itemBackground: "#ffffff",
  itemBorderColor: "#e2e8f0",
  itemBorderWidth: 1,
  itemBorderRadius: 0,
  itemPaddingX: 12,
  itemPaddingY: 10,
  itemTextColor: "#1f2430",
  itemFontSize: 14,
  showNumbers: true,
  numberPosition: "middle-left",
  numberColor: "#ffffff",
  numberBackground: "#2563eb",
  useBackgroundImage: false,
  backgroundImageUrl: "",
  backgroundImageFit: "cover",
  backgroundImagePosition: "center center",
  backgroundImageOpacity: 1,
};

export const DEFAULT_MATCHING_POINT_APPEARANCE: MatchingPointAppearance = {
  pointSize: 16,
  sourceColor: "#2563eb",
  targetColor: "#10b981",
  sourceBorderColor: "#1d4ed8",
  targetBorderColor: "#059669",
  borderWidth: 2,
  borderRadius: 0, // 0% = kare (default)
  enableHoverEffect: false, // Default olarak kapalı
  connectionColor: "#2563eb",
  connectionWidth: 2,
  connectionStyle: "solid",
};

export const DEFAULT_IMAGE_STYLE: ImageStyle = {
  url: "",
  alt: "", // overridden by resolveImageStyle via t("appearance.image.alt")
  objectFit: "contain",
  objectPosition: "center center",
  borderRadius: 0,
  borderWidth: 0,
  borderColor: "#000000",
  opacity: 1,
  shadow: "none",
};

export const DEFAULT_AUDIO_STYLE: AudioStyle = {
  sourceUrl: "",
  label: "", // overridden by resolveAudioStyle via t("appearance.audio.label")
  autoPlay: false,
  loop: false,
  showControls: false,
  showIcon: true,
  showLabel: true,
  backgroundColor: "#f8fafc",
  borderColor: "#cbd5e1",
  borderWidth: 1,
  borderRadius: 0,
  textColor: "#1f2430",
  iconColor: "#2563eb",
  paddingX: 16,
  paddingY: 12,
  playOnHover: false,
};

export const DEFAULT_VIDEO_STYLE: VideoStyle = {
  sourceUrl: "",
  label: "Video",
  autoPlay: false,
  loop: false,
  showControls: true,
  showElementsAfterVideo: [],
  borderColor: "#cbd5e1",
  borderWidth: 0,
  borderRadius: 0,
  marginX: 0,
  marginY: 0,
};

export const DEFAULT_PUSH_TO_TALK_APPEARANCE: PushToTalkAppearance = {
  // Translatable strings — all overridden by resolvePushToTalkAppearance via getT()
  instructionText: "",
  idleButtonText: "",
  listeningButtonText: "",
  processingButtonText: "",
  transcriptionLabel: "",
  transcriptionPlaceholder: "",
  errorPreviewMessage: "",
  language: "tr-TR",
  maxDurationSeconds: 20,
  backgroundColor: "#f8fafc",
  borderColor: "#cbd5e1",
  borderWidth: 1,
  borderRadius: 0,
  textColor: "#1f2430",
  buttonIdleColor: "#2563eb",
  buttonIdleTextColor: "#ffffff",
  buttonListeningColor: "#dc2626",
  buttonListeningTextColor: "#ffffff",
  buttonProcessingColor: "#334155",
  buttonProcessingTextColor: "#ffffff",
  showTranscription: true,
  showMicButton: true,
  showErrorAlert: true,
  showManualInput: true,
  showManualInputLabel: true,
  waveformColor: "#f97316",
  buttonContentType: "icon",
  buttonImageUrl: "",
  buttonImageUrlIdle: "",
  buttonImageUrlListening: "",
  buttonSizePx: 72,
  buttonContentSizePx: 28,
  buttonTextFontSize: 14,
  buttonCornerRadiusPx: 999,
  buttonBorderWidthPx: 0,
  buttonBorderColor: "transparent",
  buttonPaddingPx: 0,
  buttonShape: "circle",
  errorBackgroundColor: "#fef2f2",
  errorBorderColor: "#fecaca",
  errorTextColor: "#dc2626",
  editorPreviewStatus: "idle",
};

export const DEFAULT_MATHTYPE_STYLE: MathTypeStyle = {
  mathml: "",
  fontSize: 20,
  color: "#1f2430",
  backgroundColor: "transparent",
  borderRadius: 0,
  borderWidth: 0,
  borderColor: "transparent",
  paddingX: 8,
  paddingY: 8,
};

// export const DEFAULT_TIMER_STYLE: TimerStyle = {
//   duration: 60, // 60 seconds
//   format: "mm:ss",
//   allowContinueAfterExpiry: true,
//   autoSubmitOnExpiry: false,
//   fontFamily: "Inter",
//   fontSize: 32,
//   fontWeight: 700,
//   color: "#1f2430",
//   backgroundColor: "transparent",
//   paddingX: 12,
//   paddingY: 8,
//   borderRadius: 8,
//   borderWidth: 2,
//   borderColor: "#2563eb",
//   textAlign: "center",
// };

// export const DEFAULT_SUBMIT_BUTTON_STYLE: SubmitButtonStyle = {
//   text: "Gönder",
//   fontFamily: "Inter",
//   fontSize: 16,
//   fontWeight: 600,
//   textColor: "#ffffff",
//   backgroundColor: "#2563eb",
//   paddingX: 24,
//   paddingY: 12,
//   borderRadius: 8,
//   borderWidth: 0,
//   borderColor: "#000000",
// };

export const DEFAULT_CHOICE_APPEARANCE_EXTENDED: ChoiceAppearanceExtended = {
  // Layout
  layout: "vertical",
  gridColumns: 2,
  uniformOptionWidth: false,

  // Container/Group styles
  groupBackground: "transparent",
  groupBorderRadius: 0,
  groupBorderWidth: 0,
  groupBorderColor: "transparent",
  groupPadding: 0,
  groupShadow: "none",

  // Text styles
  fontFamily: "Inter",
  fontSize: 16,
  fontWeight: 400,

  // Option container styles
  optionBackground: "#ffffff",
  optionHoverBackground: "#f8fafc",
  optionActiveBackground: "#e1e9ff",
  optionTextColor: "#1f2430",
  optionHoverTextColor: "#1f2430",
  optionActiveTextColor: "#1f2430",
  optionBorderWidth: 1,
  optionBorderColor: "#cbd5e1",
  optionHoverBorderColor: "#94a3b8",
  optionActiveBorderColor: "#2563eb",
  optionBorderRadius: 0,
  optionPaddingX: 16,
  optionPaddingY: 12,
  optionSpacing: 10,
  optionShadow: "none",

  // Option alignment
  optionAlign: "left",
  optionJustify: "start",
  optionPrefix: "none",

  // Indicator (checkbox/radio) styles
  showIndicator: true,
  indicatorType: "radio",
  indicatorPosition: "leading",
  indicatorColor: "#2563eb",
  indicatorActiveColor: "#1d4ed8",
  indicatorBorderColor: "#cbd5e1",
  indicatorActiveBorderColor: "#2563eb",
  indicatorSize: 20,
  indicatorBorderWidth: 2,
  indicatorBorderRadius: 0, // For checkbox, will be 50% for radio

  // Custom indicator icons
  indicatorCustomIconUrl: undefined,
  indicatorCustomIconCheckedUrl: undefined,

  // Option icon/image styles
  showOptionIcon: true,
  optionIconPosition: "left",
  optionIconSize: 24,
  optionImageSize: 48,
  optionImageObjectFit: "cover",
  optionImageBorderRadius: 0,

  // Text alignment
  textAlign: "left",
};

export function resolveTextStyle(element: EditorElement): TextStyle {
  const data = element.data as { style?: Partial<TextStyle> };
  return {
    ...DEFAULT_TEXT_STYLE,
    ...(data.style ?? {}),
  };
}

export function resolveChoiceAppearance(
  element: EditorElement
): ChoiceAppearance {
  const data = element.data as { appearance?: Partial<ChoiceAppearance> };
  return {
    ...DEFAULT_CHOICE_APPEARANCE,
    ...(data.appearance ?? {}),
  };
}

export function resolveDragSourceAppearance(
  element: EditorElement
): DragSourceAppearance {
  const data = element.data as {
    appearance?: Partial<DragSourceAppearance> & {
      textAlign?: "left" | "center" | "right";
    };
    showLabel?: boolean | number | string;
    clonable?: boolean | number | string;
  };
  const { textAlign: legacyTextAlign, ...appearanceRest } =
    data.appearance ?? {};
  const resolved: DragSourceAppearance = {
    ...DEFAULT_DRAG_SOURCE_APPEARANCE,
    ...appearanceRest,
  };

  // Sync data-level showLabel and clonable to appearance
  // Data level takes precedence for these properties
  if ("showLabel" in data) {
    resolved.showLabel =
      data.showLabel !== false &&
      data.showLabel !== 0 &&
      data.showLabel !== "0";
  }
  if ("clonable" in data) {
    resolved.clonable =
      data.clonable === true || data.clonable === 1 || data.clonable === "1";
  }

  // Convert useImage from string "0"/"1" to boolean if needed
  if (resolved.useImage !== undefined) {
    if (typeof resolved.useImage === "string") {
      resolved.useImage = resolved.useImage === "1";
    } else if (typeof resolved.useImage === "number") {
      resolved.useImage = resolved.useImage === 1;
    }
  }

  // Normalize showNumbering
  if (resolved.showNumbering !== undefined) {
    if (typeof resolved.showNumbering === "string") {
      resolved.showNumbering = resolved.showNumbering === "1";
    } else if (typeof resolved.showNumbering === "number") {
      resolved.showNumbering = resolved.showNumbering === 1;
    }
  }

  if (resolved.numberingFontSize !== undefined) {
    if (typeof resolved.numberingFontSize === "string") {
      const parsed = parseFloat(resolved.numberingFontSize);
      resolved.numberingFontSize = Number.isNaN(parsed)
        ? DEFAULT_DRAG_SOURCE_APPEARANCE.numberingFontSize
        : parsed;
    }
  }
  if (resolved.numberingBorderRadius !== undefined) {
    if (typeof resolved.numberingBorderRadius === "string") {
      const parsed = parseFloat(resolved.numberingBorderRadius);
      resolved.numberingBorderRadius = Number.isNaN(parsed)
        ? DEFAULT_DRAG_SOURCE_APPEARANCE.numberingBorderRadius
        : Math.max(0, parsed);
    }
  }
  if (resolved.numberingPosition) {
    const allowedPositions = new Set([
      "top-left",
      "top-right",
      "bottom-right",
      "bottom-left",
      "center",
    ]);
    if (!allowedPositions.has(resolved.numberingPosition)) {
      resolved.numberingPosition = DEFAULT_DRAG_SOURCE_APPEARANCE.numberingPosition;
    }
  }

  if (!resolved.labelPosition && legacyTextAlign) {
    switch (legacyTextAlign) {
      case "left":
        resolved.labelPosition = "center";
        break;
      case "right":
        resolved.labelPosition = "center";
        break;
      default:
        resolved.labelPosition = "center";
        break;
    }
  }

  if (!resolved.labelPosition) {
    resolved.labelPosition = "center";
  }

  return resolved;
}

export function resolveDropTargetAppearance(
  element: EditorElement
): DropTargetAppearance {
  const data = element.data as { appearance?: Partial<DropTargetAppearance> };
  const resolved: DropTargetAppearance = {
    ...DEFAULT_DROP_TARGET_APPEARANCE,
    ...(data.appearance ?? {}),
  };

  // Convert useBackgroundImage from string "0"/"1" to boolean if needed
  if (resolved.useBackgroundImage !== undefined) {
    if (typeof resolved.useBackgroundImage === "string") {
      resolved.useBackgroundImage = resolved.useBackgroundImage === "1";
    } else if (typeof resolved.useBackgroundImage === "number") {
      resolved.useBackgroundImage = resolved.useBackgroundImage === 1;
    }
  }

  return resolved;
}

export function resolveOrderListAppearance(
  element: EditorElement
): OrderListAppearance {
  const data = element.data as { appearance?: Partial<OrderListAppearance> };
  const appearance = data.appearance ?? {};
  return {
    ...DEFAULT_ORDER_LIST_APPEARANCE,
    ...appearance,
    // Explicitly handle showNumbers to preserve false values
    showNumbers:
      appearance.showNumbers !== undefined
        ? appearance.showNumbers
        : DEFAULT_ORDER_LIST_APPEARANCE.showNumbers,
  };
}

export function resolveImageStyle(element: EditorElement): ImageStyle {
  const t = getT();
  const data = element.data as { style?: Partial<ImageStyle> };
  return {
    ...DEFAULT_IMAGE_STYLE,
    alt: t("appearance.image.alt"),
    ...(data.style ?? {}),
  };
}

export function resolveAudioStyle(element: EditorElement): AudioStyle {
  const t = getT();
  const data = element.data as { style?: Partial<AudioStyle> };
  const style = data.style ?? {};

  // Helper function to normalize boolean values
  const normalizeBoolean = (
    value: boolean | number | string | undefined,
    defaultValue: boolean
  ): boolean => {
    if (value === undefined || value === null) return defaultValue;
    if (value === false || value === 0 || value === "0" || value === "")
      return false;
    if (value === true || value === 1 || value === "1") return true;
    return Boolean(value);
  };

  return {
    ...DEFAULT_AUDIO_STYLE,
    label: t("appearance.audio.label"),
    ...style,
    autoPlay: normalizeBoolean(style.autoPlay, DEFAULT_AUDIO_STYLE.autoPlay),
    loop: normalizeBoolean(style.loop, DEFAULT_AUDIO_STYLE.loop),
    showControls: normalizeBoolean(
      style.showControls,
      DEFAULT_AUDIO_STYLE.showControls
    ),
    showIcon: normalizeBoolean(style.showIcon, DEFAULT_AUDIO_STYLE.showIcon),
    showLabel: normalizeBoolean(style.showLabel, DEFAULT_AUDIO_STYLE.showLabel),
    playOnHover: normalizeBoolean(
      style.playOnHover,
      DEFAULT_AUDIO_STYLE.playOnHover ?? false
    ),
  };
}

export function resolveVideoStyle(element: EditorElement): VideoStyle {
  const data = element.data as { style?: Partial<VideoStyle> };
  const style = data.style ?? {};

  // Helper function to normalize boolean values
  const normalizeBoolean = (
    value: boolean | number | string | undefined,
    defaultValue: boolean
  ): boolean => {
    if (value === undefined || value === null) return defaultValue;
    if (value === false || value === 0 || value === "0" || value === "")
      return false;
    if (value === true || value === 1 || value === "1") return true;
    return Boolean(value);
  };

  return {
    ...DEFAULT_VIDEO_STYLE,
    ...style,
    autoPlay: normalizeBoolean(style.autoPlay, DEFAULT_VIDEO_STYLE.autoPlay),
    loop: normalizeBoolean(style.loop, DEFAULT_VIDEO_STYLE.loop),
    showControls: normalizeBoolean(
      style.showControls,
      DEFAULT_VIDEO_STYLE.showControls
    ),
  };
}

export function resolvePushToTalkAppearance(
  element: EditorElement
): PushToTalkAppearance {
  const t = getT();
  const data = element.data as {
    appearance?: Partial<PushToTalkAppearance>;
  };
  const merged = {
    ...DEFAULT_PUSH_TO_TALK_APPEARANCE,
    instructionText: t("appearance.pushToTalk.instructionText"),
    idleButtonText: t("appearance.pushToTalk.idleButtonText"),
    listeningButtonText: t("appearance.pushToTalk.listeningButtonText"),
    processingButtonText: t("appearance.pushToTalk.processingButtonText"),
    transcriptionLabel: t("appearance.pushToTalk.transcriptionLabel"),
    transcriptionPlaceholder: t("appearance.pushToTalk.transcriptionPlaceholder"),
    errorPreviewMessage: t("appearance.pushToTalk.errorPreviewMessage"),
    ...(data.appearance ?? {}),
  };
  const oldPlaceholder = t("appearance.pushToTalk.oldPlaceholder");
  const newPlaceholder = t("appearance.pushToTalk.transcriptionPlaceholder");
  const currentPlaceholder = (merged.transcriptionPlaceholder ?? "").trim();
  if (!currentPlaceholder || currentPlaceholder === oldPlaceholder) {
    merged.transcriptionPlaceholder = newPlaceholder;
  }
  return merged;
}

export function resolveMathTypeStyle(element: EditorElement): MathTypeStyle {
  const data = element.data as { style?: Partial<MathTypeStyle> };
  return {
    ...DEFAULT_MATHTYPE_STYLE,
    ...(data.style ?? {}),
  };
}

// export function resolveTimerStyle(element: EditorElement): TimerStyle {
//   const data = element.data as { style?: Partial<TimerStyle> };
//   return {
//     ...DEFAULT_TIMER_STYLE,
//     ...(data.style ?? {}),
//   };
// }

// export function resolveSubmitButtonStyle(element: EditorElement): SubmitButtonStyle {
//   const data = element.data as { style?: Partial<SubmitButtonStyle> };
//   return {
//     ...DEFAULT_SUBMIT_BUTTON_STYLE,
//     ...(data.style ?? {}),
//   };
// }

export function resolveChoiceAppearanceExtended(
  element: EditorElement
): ChoiceAppearanceExtended {
  const data = element.data as {
    appearance?: Partial<ChoiceAppearanceExtended>;
  };
  return {
    ...DEFAULT_CHOICE_APPEARANCE_EXTENDED,
    ...(data.appearance ?? {}),
  };
}

export function resolveMatchingPointAppearance(
  element: EditorElement
): MatchingPointAppearance {
  const data = element.data as {
    appearance?: Partial<MatchingPointAppearance>;
  };
  return {
    ...DEFAULT_MATCHING_POINT_APPEARANCE,
    ...(data.appearance ?? {}),
  };
}

export function mapLabelPosition(labelPosition: LabelPosition): {
  alignItems: "flex-start" | "center" | "flex-end";
  justifyContent: "flex-start" | "center" | "flex-end";
  textAlign: "left" | "center" | "right";
} {
  const labelPositionMap: Record<
    LabelPosition,
    {
      alignItems: "flex-start" | "center" | "flex-end";
      justifyContent: "flex-start" | "center" | "flex-end";
      textAlign: "left" | "center" | "right";
    }
  > = {
    "top-left": {
      alignItems: "flex-start",
      justifyContent: "flex-start",
      textAlign: "left",
    },
    "top-center": {
      alignItems: "center",
      justifyContent: "flex-start",
      textAlign: "center",
    },
    "top-right": {
      alignItems: "flex-end",
      justifyContent: "flex-start",
      textAlign: "right",
    },
    center: {
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
    },
    "bottom-left": {
      alignItems: "flex-start",
      justifyContent: "flex-end",
      textAlign: "left",
    },
    "bottom-center": {
      alignItems: "center",
      justifyContent: "flex-end",
      textAlign: "center",
    },
    "bottom-right": {
      alignItems: "flex-end",
      justifyContent: "flex-end",
      textAlign: "right",
    },
  };

  return labelPositionMap[labelPosition] || labelPositionMap.center;
}

export const DEFAULT_FILL_IN_THE_BLANKS_APPEARANCE: FillInTheBlanksAppearance =
  {
    fontFamily: "Inter",
    fontSize: 18,
    fontWeight: 400,
    color: "#1f2430",
    lineHeight: 1.6,
    backgroundColor: "transparent",
    paddingX: 12,
    paddingY: 12,
    borderRadius: 0,
    borderWidth: 0,
    borderColor: "transparent",
    italic: false,
    underline: false,
    textAlign: "left",
    letterSpacing: 0,
    textTransform: "none",
    shadow: "none",

    // Input styles
    inputBackgroundColor: "#ffffff",
    inputBorderColor: "#cbd5e1",
    inputBorderWidth: 1,
    inputBorderRadius: 0,
    inputTextColor: "#1f2430",
    inputPaddingX: 8,
    inputPaddingY: 4,
    inputMinWidth: 60,
    inputDefaultWidth: undefined, // Let it grow with content or minWidth
    inputTextAlign: "center",
    inputFontWeight: 400,
    inputFontFamily: "Inter",
    inputFontSize: 18,
    inputItalic: false,
    inputUnderline: false,
    inputLetterSpacing: 0,
    inputBorderStyle: "full",
    hideAnswers: false, // Show answers in canvas mode by default
  };

export function resolveFillInTheBlanksAppearance(
  element: EditorElement
): FillInTheBlanksAppearance {
  const data = element.data as {
    appearance?: Partial<FillInTheBlanksAppearance>;
  };
  const userAppearance = data.appearance ?? {};

  // Extract input styles separately - they should only be applied if explicitly set
  const {
    inputBackgroundColor,
    inputBorderColor,
    inputBorderWidth,
    inputBorderRadius,
    inputTextColor,
    inputPaddingX,
    inputPaddingY,
    inputMinWidth,
    inputDefaultWidth,
    inputTextAlign,
    inputFontWeight,
    inputFontFamily,
    inputFontSize,
    inputItalic,
    inputUnderline,
    inputLetterSpacing,
    inputBorderStyle,
    ...textStyles
  } = userAppearance;

  // Text styles: use defaults if not provided
  // Input styles: only use if explicitly provided, otherwise undefined
  return {
    ...DEFAULT_FILL_IN_THE_BLANKS_APPEARANCE,
    ...textStyles,
    inputBackgroundColor,
    inputBorderColor,
    inputBorderWidth,
    inputBorderRadius,
    inputTextColor,
    inputPaddingX,
    inputPaddingY,
    inputMinWidth,
    inputDefaultWidth,
    inputTextAlign,
    inputFontWeight,
    inputFontFamily,
    inputFontSize,
    inputItalic,
    inputUnderline,
    inputLetterSpacing,
    inputBorderStyle,
  };
}

export const DEFAULT_PAINT_AREA_APPEARANCE: PaintAreaAppearance = {
  backgroundColor: "#f8fafc",
  borderColor: "#cbd5e1",
  borderWidth: 1,
  borderStyle: "solid",
  borderRadius: 0,
  name: "",
  shapeType: "star",
  currentColor: undefined,
};

export function resolvePaintAreaAppearance(
  element: EditorElement
): PaintAreaAppearance {
  const data = element.data as {
    appearance?: Partial<PaintAreaAppearance>;
  };
  const userAppearance = data.appearance ?? {};
  return {
    ...DEFAULT_PAINT_AREA_APPEARANCE,
    ...userAppearance,
    shapeType:
      userAppearance.shapeType ?? DEFAULT_PAINT_AREA_APPEARANCE.shapeType,
  };
}

export const DEFAULT_PAINT_SWATCH_APPEARANCE: PaintSwatchAppearance = {
  color: "#ff0000",
  size: 40,
  borderRadius: 0,
  borderWidth: 2,
  borderColor: "#cbd5e1",
  showLabel: false,
  label: "",
  labelColor: "#1f2430",
  labelFontSize: 12,
  maxUses: 0,
};

export function resolvePaintSwatchAppearance(
  element: EditorElement
): PaintSwatchAppearance {
  const data = element.data as {
    appearance?: Partial<PaintSwatchAppearance>;
  };
  const userAppearance = data.appearance ?? {};
  return {
    ...DEFAULT_PAINT_SWATCH_APPEARANCE,
    ...userAppearance,
  };
}

export const DEFAULT_TABLE_APPEARANCE: TableAppearance = {
  // Outer border
  showOuterBorder: true,
  outerBorderColor: "#cbd5e1",
  outerBorderWidth: 2,
  outerBorderStyle: "solid",
  borderRadius: 0,

  // Inner borders (cell borders)
  showCellBorders: true,
  cellBorderColor: "#e2e8f0",
  cellBorderWidth: 1,
  cellBorderStyle: "solid",

  // Cell styling
  cellBackgroundColor: "#ffffff",
  cellPadding: 12,
  rowSpacing: 0,
  columnSpacing: 0,

  // Header styling
  headerBackgroundColor: "#f1f5f9",
  headerTextColor: "#1f2937",
  headerFontWeight: 600,

  // Alternate row styling
  alternateRowColor: false,
  alternateRowBackgroundColor: "#f8fafc",

  // Legacy (backward compatibility)
  borderColor: "#cbd5e1",
  borderWidth: 1,
  borderStyle: "solid",
  showBorders: true,
};

export function resolveTableAppearance(
  element: EditorElement
): TableAppearance {
  const data = element.data as {
    appearance?: Partial<TableAppearance>;
  };
  return {
    ...DEFAULT_TABLE_APPEARANCE,
    ...(data.appearance ?? {}),
  };
}
